import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {SchemaPropertyType} from "@vesta/vos-types/modules/platformLevel/storageDatabases/domain/StorageDatabaseSchema";
import {
    getSchemaPropertyTypeFromOttopilotEventContainerTemplateAdditionalColumnType,
    OttopilotEventContainerTemplateAdditionalColumnType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateAdditionalColumns";
import {z as zod} from "zod";

export enum DomainAggregateBuildType {
    clickhouse = "clickhouse",
    sql = "sql",
}

export enum DomainAggregateEntityType {
    materializedView = "materializedView",
    table = "table",
    view = "view",
}

export enum DomainAggregateRefreshType {
    full = "full",
    partial = "partial",
}

export enum DomainAggregateFieldConfigurationType {
    analytical = "Analytical",
    operational = "Operational",
}

export enum DomainAggregateTemplateKind {
    event = "event",
    eventContainer = "eventContainer",
}

export enum DomainAggregateSourceColumnKind {
    additional = "additional",
    fixed = "fixed",
}

export enum DomainAggregateSelectColumnType {
    select = "b7e4c2a1-9f30-4d58-a6b1-3c8e7f0d2a94",
}

export type DomainAggregateSelectOption = {
    displayName: string;
    id: Uuid;
};

export const domainAggregateRequiredFixedColumns: Record<string, SchemaPropertyType> = {
    created_at: SchemaPropertyType.timestamp,
    created_by: SchemaPropertyType.uuid,
    id: SchemaPropertyType.uuid,
    revision_id: SchemaPropertyType.uuid,
    status: SchemaPropertyType.uuid,
    template_id: SchemaPropertyType.uuid,
    updated_at: SchemaPropertyType.timestamp,
    updated_by: SchemaPropertyType.uuid,
};

const domainAggregateDbColumnNamePattern = /^[a-z][a-z0-9_]*$/;

export const domainAggregateDbColumnNameValidator = zod.string().refine(
    (value) => domainAggregateDbColumnNamePattern.test(value) === true || uuidZodValidator.safeParse(value).success === true,
    "dbColumnName must be snake_case or a UUID",
);

export type DomainAggregateAnalyticalFieldConfiguration = {
    resolver: string;
    type: DomainAggregateFieldConfigurationType.analytical;
};

export type DomainAggregateOperationalFieldConfiguration = {
    field: Uuid | string;
    sourceKind: DomainAggregateSourceColumnKind;
    templateId: Uuid;
    templateType: DomainAggregateTemplateKind;
    type: DomainAggregateFieldConfigurationType.operational;
};

type DomainAggregateFieldBase = {
    configuration: DomainAggregateAnalyticalFieldConfiguration | DomainAggregateOperationalFieldConfiguration;
    dbColumnName: string;
    displayName: string;
    id: Uuid;
};

export type DomainAggregateField =
    | (DomainAggregateFieldBase & {
          columnType: Exclude<
              OttopilotEventContainerTemplateAdditionalColumnType,
              | OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject
              | OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects
          >;
      })
    | (DomainAggregateFieldBase & {
          columnConfiguration: {
              userDefinedObjectTemplateId: Uuid;
          };
          columnType:
              | OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject
              | OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects;
      })
    | (DomainAggregateFieldBase & {
          columnConfiguration: {
              options: Array<DomainAggregateSelectOption>;
          };
          columnType: DomainAggregateSelectColumnType.select;
      });

export function getDomainAggregateDbColumnType(
    columnType: DomainAggregateField["columnType"],
): SchemaPropertyType {
    if (columnType === DomainAggregateSelectColumnType.select) {
        return SchemaPropertyType.uuid;
    }
    return getSchemaPropertyTypeFromOttopilotEventContainerTemplateAdditionalColumnType(columnType);
}

export type DomainAggregateUacm = {
    dbColumnName: string;
    sourceTemplateId: Uuid;
    sourceTemplateType: DomainAggregateTemplateKind;
};

export type DomainAggregateConfiguration = {
    execution: {
        build: {
            entityType: DomainAggregateEntityType;
            query: string;
            type: DomainAggregateBuildType;
        };
        refresh: {
            type: DomainAggregateRefreshType;
        };
    };
    fields: Array<DomainAggregateField>;
    uacm: DomainAggregateUacm;
};

const domainAggregateAnalyticalFieldConfigurationValidator = zod.strictObject({
    resolver: zod.string().min(1),
    type: zod.literal(DomainAggregateFieldConfigurationType.analytical),
});

const domainAggregateOperationalFieldConfigurationValidator = zod.strictObject({
    field: zod.union([uuidZodValidator, zod.string().regex(domainAggregateDbColumnNamePattern)]),
    sourceKind: zod.enum(DomainAggregateSourceColumnKind),
    templateId: uuidZodValidator,
    templateType: zod.enum(DomainAggregateTemplateKind),
    type: zod.literal(DomainAggregateFieldConfigurationType.operational),
});

const domainAggregateFieldBaseValidator = zod.strictObject({
    configuration: zod.discriminatedUnion("type", [
        domainAggregateAnalyticalFieldConfigurationValidator,
        domainAggregateOperationalFieldConfigurationValidator,
    ]),
    dbColumnName: domainAggregateDbColumnNameValidator,
    displayName: zod.string().min(1),
    id: uuidZodValidator,
});

const domainAggregateFieldWithoutColumnConfigurationValidator = domainAggregateFieldBaseValidator.extend({
    columnType: zod.enum([
        OttopilotEventContainerTemplateAdditionalColumnType.btlActivity,
        OttopilotEventContainerTemplateAdditionalColumnType.email,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyCountries,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaCities,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaPincodes,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaStates,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaZones,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyLatitude,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyLongitude,
        OttopilotEventContainerTemplateAdditionalColumnType.indianPhoneNumber,
        OttopilotEventContainerTemplateAdditionalColumnType.number,
        OttopilotEventContainerTemplateAdditionalColumnType.ou,
        OttopilotEventContainerTemplateAdditionalColumnType.product,
        OttopilotEventContainerTemplateAdditionalColumnType.role,
        OttopilotEventContainerTemplateAdditionalColumnType.text,
        OttopilotEventContainerTemplateAdditionalColumnType.timestamp,
        OttopilotEventContainerTemplateAdditionalColumnType.user,
        OttopilotEventContainerTemplateAdditionalColumnType.uuid,
    ]),
});

const domainAggregateUserDefinedObjectFieldValidator = domainAggregateFieldBaseValidator.extend({
    columnConfiguration: zod.strictObject({
        userDefinedObjectTemplateId: uuidZodValidator,
    }),
    columnType: zod.literal(OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject),
});

const domainAggregateUserDefinedObjectsFieldValidator = domainAggregateFieldBaseValidator.extend({
    columnConfiguration: zod.strictObject({
        userDefinedObjectTemplateId: uuidZodValidator,
    }),
    columnType: zod.literal(OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects),
});

const domainAggregateSelectOptionValidator = zod.strictObject({
    displayName: zod.string().min(1),
    id: uuidZodValidator,
});

const domainAggregateSelectFieldValidator = domainAggregateFieldBaseValidator.extend({
    columnConfiguration: zod.strictObject({
        options: zod.array(domainAggregateSelectOptionValidator).min(1),
    }).superRefine((columnConfiguration, context) => {
        const optionIds = new Set<string>();
        for (let index = 0; index < columnConfiguration.options.length; index++) {
            const option = columnConfiguration.options[index];
            if (option === undefined) {
                continue;
            }
            if (optionIds.has(option.id) === true) {
                context.addIssue({
                    code: "custom",
                    message: `Duplicate select option id ${option.id}`,
                    path: ["options", index, "id"],
                });
            }
            optionIds.add(option.id);
        }
    }),
    columnType: zod.literal(DomainAggregateSelectColumnType.select),
});

const domainAggregateFieldValidator: zod.ZodType<DomainAggregateField> = zod.union([
    domainAggregateFieldWithoutColumnConfigurationValidator,
    domainAggregateUserDefinedObjectFieldValidator,
    domainAggregateUserDefinedObjectsFieldValidator,
    domainAggregateSelectFieldValidator,
]).superRefine((field, context) => {
    if (field.configuration.type !== DomainAggregateFieldConfigurationType.operational) {
        return;
    }
    if (field.configuration.sourceKind === DomainAggregateSourceColumnKind.fixed && uuidZodValidator.safeParse(field.configuration.field).success === true) {
        context.addIssue({
            code: "custom",
            message: "A fixed column field must be the snake_case column name",
            path: ["configuration", "field"],
        });
    }
    if (field.configuration.sourceKind === DomainAggregateSourceColumnKind.additional && uuidZodValidator.safeParse(field.configuration.field).success !== true) {
        context.addIssue({
            code: "custom",
            message: "An additional column field must be the column id",
            path: ["configuration", "field"],
        });
    }
});

const domainAggregateUacmValidator = zod.strictObject({
    dbColumnName: domainAggregateDbColumnNameValidator,
    sourceTemplateId: uuidZodValidator,
    sourceTemplateType: zod.enum(DomainAggregateTemplateKind),
});

export const domainAggregateConfigurationValidator: zod.ZodType<DomainAggregateConfiguration> = zod.strictObject({
    execution: zod.strictObject({
        build: zod.strictObject({
            entityType: zod.enum(DomainAggregateEntityType),
            query: zod.string().min(1),
            type: zod.enum(DomainAggregateBuildType),
        }),
        refresh: zod.strictObject({
            type: zod.enum(DomainAggregateRefreshType),
        }),
    }),
    fields: zod.array(domainAggregateFieldValidator).min(1),
    uacm: domainAggregateUacmValidator,
}).superRefine((configuration, context) => {
    const fieldIds = new Set<string>();
    const columnNames = new Set<string>();
    for (let index = 0; index < configuration.fields.length; index++) {
        const field = configuration.fields[index];
        if (field === undefined) {
            continue;
        }
        if (fieldIds.has(field.id) === true) {
            context.addIssue({
                code: "custom",
                message: `Duplicate field id ${field.id}`,
                path: ["fields", index, "id"],
            });
        }
        fieldIds.add(field.id);
        if (columnNames.has(field.dbColumnName) === true) {
            context.addIssue({
                code: "custom",
                message: `Duplicate dbColumnName ${field.dbColumnName}`,
                path: ["fields", index, "dbColumnName"],
            });
        }
        columnNames.add(field.dbColumnName);
        const requiredType = domainAggregateRequiredFixedColumns[field.dbColumnName];
        const derivedType = getDomainAggregateDbColumnType(field.columnType);
        if (requiredType !== undefined && derivedType !== requiredType) {
            context.addIssue({
                code: "custom",
                message: `Fixed column ${field.dbColumnName} must use a columnType that stores ${requiredType}`,
                path: ["fields", index, "columnType"],
            });
        }
    }
    for (const requiredColumnName of Object.keys(domainAggregateRequiredFixedColumns)) {
        if (columnNames.has(requiredColumnName) !== true) {
            context.addIssue({
                code: "custom",
                message: `Fixed column ${requiredColumnName} is required`,
                path: ["fields"],
            });
        }
    }
    if (columnNames.has(configuration.uacm.dbColumnName) !== true) {
        context.addIssue({
            code: "custom",
            message: `uacm.dbColumnName ${configuration.uacm.dbColumnName} is not a view field`,
            path: ["uacm", "dbColumnName"],
        });
    }
});

export function getDomainAggregateExecutionRejection(configuration: DomainAggregateConfiguration): string | null {
    if (configuration.execution.build.type !== DomainAggregateBuildType.sql) {
        return "400: Only sql build type is supported";
    }
    if (configuration.execution.build.entityType !== DomainAggregateEntityType.view) {
        return "400: Only view entity type is supported";
    }
    if (configuration.execution.refresh.type !== DomainAggregateRefreshType.full) {
        return "400: Only full refresh is supported";
    }
    return null;
}
